/* /ponude - everything the salon is currently running, on one page.

   Three offers, and the order on the page is the order of importance: the
   September group of the basic course takes the big card, the one-on-one
   advanced course the middle one, the "Posle mora" hair promotion the small
   one. A visitor who reads only the first card has still read the one that
   matters most, and Google reads one dominant signal instead of three equal
   ones.

   Every number and date is interpolated from src/config/educations.mjs and
   src/config/offers.mjs - see the note at the top of offers.mjs for the order
   to edit them in. Nothing volatile is hard-coded in the locales. */

import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Bredcrumb from '../../HomeMain/Bredcrumb';
import {
  Section, Heading, P, NumberedList, Callout, buttonStyle,
  BRAND, GOLD, INK, BODY, CREAM, serif, sans,
} from '../../ui/blocks';
import { BASIC_COURSE, ADVANCED_COURSE } from '../../../config/educations.mjs';
import { OFFER, ADVANCED_OFFER, HAIR_OFFER } from '../../../config/offers.mjs';
import { PHONE_DISPLAY, PHONE_TEL } from '../../../config/emailjs';
import { linkTo } from '../../../i18n/lang.mjs';

/** The salon's Google Business profile - where the 215 reviews live. */
const GOOGLE_PROFILE = 'https://www.google.com/maps?cid=0xeec35ad54ebb4ce';

/** 40.000 for sr/de, 40,000 for en. A separator read the wrong way turns
    forty thousand dinars into forty. */
const num = (value, lang) => value.toLocaleString(lang === 'en' ? 'en-US' : 'de-DE');

/* Card geometry per rank. Only these four values change between the big, the
   middle and the small card - everything else is shared, so the three cards
   cannot drift apart stylistically.

   maxWidth is measured against the 852px the Section's container actually
   gives them (900px minus its padding), so the widths have to sit under that
   to read as a step down at all - the large card simply fills it. */
const SIZES = {
  large: { padding: 'clamp(26px, 5vw, 48px)', title: 'clamp(1.9rem, 4vw, 2.75rem)', rule: '4px', maxWidth: 'none' },
  medium: { padding: 'clamp(24px, 4vw, 38px)', title: 'clamp(1.6rem, 3.2vw, 2.15rem)', rule: '3px', maxWidth: '780px' },
  small: { padding: 'clamp(22px, 3.5vw, 32px)', title: 'clamp(1.4rem, 2.8vw, 1.8rem)', rule: '2px', maxWidth: '680px' },
};

/** The shell every offer card shares: label pill, title, subtitle, body. */
const OfferCard = ({ size, label, title, subtitle, children }) => {
  const s = SIZES[size];
  return (
    <article
      style={{
        background: '#fff',
        border: '1px solid rgba(144,81,15,0.16)',
        borderTop: `${s.rule} solid ${BRAND}`,
        borderRadius: '24px',
        padding: s.padding,
        maxWidth: s.maxWidth,
        boxShadow: '0 18px 48px rgba(26,13,5,0.07)',
      }}
    >
      <p
        style={{
          display: 'inline-block',
          fontFamily: sans,
          fontSize: '11px',
          fontWeight: 700,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: BRAND,
          background: 'rgba(144,81,15,0.10)',
          borderRadius: '9999px',
          padding: '7px 16px',
          margin: '0 0 18px',
        }}
      >
        {label}
      </p>

      <h2
        style={{
          fontFamily: serif,
          fontSize: s.title,
          fontWeight: 600,
          lineHeight: 1.15,
          color: INK,
          margin: '0 0 12px',
        }}
      >
        {title}
      </h2>

      <p
        style={{
          fontFamily: sans,
          fontSize: '15px',
          fontWeight: 700,
          letterSpacing: '0.04em',
          color: GOLD,
          margin: '0 0 28px',
        }}
      >
        {subtitle}
      </p>

      {children}
    </article>
  );
};

/** The dashed "what you get" list, shared by all three cards. */
const IncludedList = ({ items }) => (
  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '12px' }}>
    {items.map((item, i) => (
      <li
        key={i}
        style={{
          display: 'flex',
          gap: '12px',
          fontFamily: sans,
          fontSize: '16px',
          lineHeight: 1.7,
          color: BODY,
        }}
      >
        <span aria-hidden="true" style={{ color: BRAND, fontWeight: 700, flexShrink: 0 }}>-</span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

/** Sub-heading inside a card ("Šta ulazi u cenu", "Uz kurs dobijaš"). */
const CardHeading = ({ children }) => (
  <h3 style={{ fontFamily: serif, fontSize: '22px', fontWeight: 600, color: INK, margin: '32px 0 16px' }}>
    {children}
  </h3>
);

/** The price line, ruled off from the body above it. */
const PriceBlock = ({ label, children }) => (
  <div style={{ marginTop: '32px', paddingTop: '26px', borderTop: '1px solid rgba(144,81,15,0.14)' }}>
    <p
      style={{
        fontFamily: sans,
        fontSize: '12px',
        fontWeight: 700,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: BRAND,
        margin: '0 0 8px',
      }}
    >
      {label}
    </p>
    <p style={{ fontFamily: sans, fontSize: '19px', fontWeight: 600, lineHeight: 1.6, color: INK, margin: 0 }}>
      {children}
    </p>
  </div>
);

const Main = () => {
  const { t, i18n } = useTranslation();
  const lang = ['sr', 'en', 'de'].includes(i18n.language) ? i18n.language : 'sr';

  const startInline = BASIC_COURSE.startDateInline[lang] || BASIC_COURSE.startDateInline.sr;
  const vars = {
    startInline,
    max: BASIC_COURSE.maxAttendees,
    price: BASIC_COURSE.priceEur,
    priceRsd: num(BASIC_COURSE.priceRsdApprox, lang),
    deposit: BASIC_COURSE.depositEur,
    from: num(BASIC_COURSE.marketPriceRsd.from, lang),
    to: num(BASIC_COURSE.marketPriceRsd.to, lang),
    phone: PHONE_DISPLAY,
    advPrice: ADVANCED_COURSE.priceEur,
    advDeposit: ADVANCED_COURSE.depositEur,
    followers: num(ADVANCED_OFFER.instagramFollowers, lang),
    minSpend: num(HAIR_OFFER.minSpendRsd, lang),
    guaranteeDays: HAIR_OFFER.toneGuaranteeDays,
    hairUntil: HAIR_OFFER.validThroughLabel[lang] || HAIR_OFFER.validThroughLabel.sr,
  };

  const included = t('offers.card.included', { returnObjects: true });
  const included2 = t('offers.card2.included', { returnObjects: true, ...vars });
  const included3 = t('offers.card3.included', { returnObjects: true, ...vars });
  const steps = t('offers.howTo.steps', { returnObjects: true })
    .map((step) => step.replace('{{phone}}', PHONE_DISPLAY));

  return (
    <>
      <Bredcrumb title={t('pages.offers.title')} subtitle={t('pages.offers.subtitle')} />

      <Section>
        <P style={{ fontSize: '18px', maxWidth: '720px', marginBottom: 0 }}>
          {t('offers.intro')}
        </P>
      </Section>

      {/* ── Card 1 - the September group of the basic course ── */}
      <Section tinted style={{ paddingTop: '24px' }}>
        <OfferCard
          size="large"
          label={t('offers.card.label')}
          title={t('offers.card.title')}
          subtitle={t('offers.card.subtitle', vars)}
        >
          <P>{t('offers.card.body1')}</P>
          <P>{t('offers.card.body2', vars)}</P>

          <CardHeading>{t('offers.card.includedHeading')}</CardHeading>
          <IncludedList items={included} />

          <Callout>{t('offers.card.earlyBird', vars)}</Callout>
          <Callout tone="gold">{t('offers.card.payback', vars)}</Callout>

          <PriceBlock label={t('offers.card.priceLabel')}>
            {t('offers.card.price', vars)}
          </PriceBlock>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', marginTop: '28px' }}>
            <Link to="/educations" style={buttonStyle('solid')}>
              {t('offers.card.ctaCourse')}
            </Link>
            <a href={`tel:${PHONE_TEL}`} style={buttonStyle('outline')}>
              {t('offers.card.ctaCall')}
            </a>
          </div>
        </OfferCard>
      </Section>

      {/* ── Cards 2 and 3 - the advanced course, then the hair promotion ── */}
      <Section style={{ paddingTop: '48px' }}>
        <div style={{ display: 'grid', gap: '32px' }}>
          <OfferCard
            size="medium"
            label={t('offers.card2.label')}
            title={t('offers.card2.title')}
            subtitle={t('offers.card2.subtitle')}
          >
            <P>{t('offers.card2.body')}</P>

            <CardHeading>{t('offers.card2.includedHeading')}</CardHeading>
            <IncludedList items={included2} />

            <PriceBlock label={t('offers.card2.priceLabel')}>
              {t('offers.card2.price', vars)}
            </PriceBlock>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', marginTop: '28px' }}>
              <Link to={linkTo('/napredni-kurs-sminkanja', lang)} style={buttonStyle('solid')}>
                {t('offers.card2.cta')}
              </Link>
            </div>
          </OfferCard>

          <OfferCard
            size="small"
            label={t('offers.card3.label')}
            title={t('offers.card3.title')}
            subtitle={t('offers.card3.subtitle')}
          >
            <P>{t('offers.card3.body', vars)}</P>

            <IncludedList items={included3} />

            <P style={{ marginTop: '22px', marginBottom: 0 }}>{t('offers.card3.note', vars)}</P>

            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '14px 20px', marginTop: '28px' }}>
              <a href={`tel:${PHONE_TEL}`} style={buttonStyle('solid')}>
                {t('offers.card3.cta')}
              </a>
              <Link
                to={linkTo('/balayage', lang)}
                style={{
                  fontFamily: sans,
                  fontSize: '15px',
                  fontWeight: 600,
                  color: BRAND,
                  textDecoration: 'underline',
                  textUnderlineOffset: '4px',
                }}
              >
                {`${t('offers.card3.link')} →`}
              </Link>
            </div>
          </OfferCard>
        </div>
      </Section>

      {/* ── How to claim it ── */}
      <Section tinted>
        <Heading>{t('offers.howTo.heading')}</Heading>
        <NumberedList items={steps} />
      </Section>

      {/* ── Proof ── */}
      <Section>
        <Heading align="center">{t('offers.proof.heading')}</Heading>
        <div style={{ textAlign: 'center' }}>
          <p
            style={{
              fontFamily: serif,
              fontSize: 'clamp(3.5rem, 9vw, 5rem)',
              fontWeight: 600,
              lineHeight: 1,
              color: BRAND,
              margin: 0,
            }}
            aria-label={t('offers.proof.scoreAria')}
          >
            {t('offers.proof.score')}
          </p>
          <p style={{ fontSize: '22px', letterSpacing: '0.12em', color: GOLD, margin: '10px 0 0' }} aria-hidden="true">
            ★★★★★
          </p>
          <p style={{ fontFamily: sans, fontSize: '16px', color: BODY, margin: '14px 0 0' }}>
            {t('offers.proof.count')}
          </p>
          <a
            href={GOOGLE_PROFILE}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              marginTop: '18px',
              fontFamily: sans,
              fontSize: '14px',
              fontWeight: 600,
              letterSpacing: '0.04em',
              color: BRAND,
              textDecoration: 'underline',
              textUnderlineOffset: '4px',
            }}
          >
            {t('offers.proof.link')}
          </a>
        </div>
      </Section>

      {/* ── Closing CTA ── */}
      <section style={{ padding: '64px 0', background: CREAM }}>
        <div style={{ maxWidth: '640px', margin: '0 auto', padding: '0 24px', textAlign: 'center' }}>
          <h2
            style={{
              fontFamily: serif,
              fontSize: 'clamp(1.75rem, 3.4vw, 2.4rem)',
              fontWeight: 600,
              color: INK,
              margin: '0 0 16px',
              lineHeight: 1.2,
            }}
          >
            {t('offers.finalCta.heading')}
          </h2>
          <p style={{ fontFamily: sans, fontSize: '16px', lineHeight: 1.8, color: BODY, margin: '0 0 28px' }}>
            {t('offers.finalCta.text', vars)}
          </p>

          <a
            href={`tel:${PHONE_TEL}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '12px',
              padding: '16px 38px',
              background: BRAND,
              color: '#fff',
              fontFamily: sans,
              fontSize: '19px',
              fontWeight: 700,
              letterSpacing: '0.03em',
              borderRadius: '9999px',
              textDecoration: 'none',
              boxShadow: '0 8px 28px rgba(144,81,15,0.32)',
            }}
          >
            {PHONE_DISPLAY}
          </a>

          <p style={{ fontFamily: sans, fontSize: '15px', lineHeight: 1.8, color: BODY, margin: '24px 0 0' }}>
            {t('offers.finalCta.address')}
            <br />
            {t('footer.hours')}
          </p>
        </div>
      </section>
    </>
  );
};

export default Main;
